import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Chart, Plugin, registerables } from 'chart.js';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { MonthlyClientBar, newestBarCap } from '@cf-shared/data-model/coach-history-chart';
import { formatMonthLong, formatMonthShort } from '@cf-shared/date-format';
import { readChartTheme } from 'utils/chart-helpers';

Chart.register(...registerables);

/** The limit line's dash, as drawn on the approved mock-up. */
const CAP_LINE_DASH = [5, 4];
const CAP_LINE_WIDTH = 1.5;
const BAR_RADIUS = 4;

/**
 * The coach billing page's "Clients Over Time" chart (#6768): one stacked bar
 * per month, Active below Asleep, and for a store coach a dashed line at the
 * plan's client limit. The bars come from `buildMonthlyClientBars`
 * (`shared/src/data-model/coach-history-chart.ts`).
 *
 * The axes, grid, tick font and colours copy `cs-progress-chart`, so the two
 * read as one family; that component only draws lines, and adding bars to it
 * would change the Progress, Home and Exercise Stats charts. The rules:
 * design-docs/coach-billing-analytics.md, "Phase 3 — the coach's view".
 */
@Component({
  selector: 'cs-coach-clients-chart',
  templateUrl: 'cs-coach-clients-chart.html',
  host: { 'class': 'coach-clients-chart' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsCoachClientsChart implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas', { static: false }) chartCanvas?: ElementRef<HTMLCanvasElement>;

  /** One bar per month, oldest first. */
  @Input() bars: MonthlyClientBar[] = [];

  readonly LL = LL;
  private chart?: Chart<'bar', number[], string>;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    this.renderChart();
  }

  ngOnChanges() {
    // if the chart already exists, redraw it with the new bars
    if (this.chart) {
      this.renderChart();
    }
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  /** What a screen reader hears instead of the picture. */
  get ariaLabel(): string {
    if (this.bars.length === 0) {
      return '';
    }
    const from = formatMonthLong(this.bars[0].monthStart, BOOT_LOCALE);
    const to = formatMonthLong(this.bars[this.bars.length - 1].monthStart, BOOT_LOCALE);
    const values = this.bars.map(bar => String(bar.active + bar.asleep)).join(', ');
    const cap = newestBarCap(this.bars);
    return cap === undefined
      ? LL.coach.billing.recordedHistory.chartAria({ from, to, values })
      : LL.coach.billing.recordedHistory.chartAriaWithCap({ from, to, values, cap });
  }

  private renderChart() {
    const canvas = this.chartCanvas?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    this.chart?.destroy();

    // colours and type come from the theme at render time, as in cs-progress-chart
    const rootStyles = getComputedStyle(document.documentElement);
    const activeColor = rootStyles.getPropertyValue('--chart-primary').trim();
    const asleepColor = rootStyles.getPropertyValue('--color-gray-500').trim();
    const theme = readChartTheme(this.el.nativeElement);

    const caps = this.bars.map(bar => bar.cap);
    const yMax = this.niceAxisMax();
    const barStyle = { borderRadius: BAR_RADIUS, borderSkipped: false, categoryPercentage: 0.8, barPercentage: 0.65 };

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.bars.map(bar => formatMonthShort(bar.monthStart, BOOT_LOCALE)),
        datasets: [
          { label: LL.coach.billing.recordedHistory.active(), data: this.bars.map(bar => bar.active), backgroundColor: activeColor, stack: 'clients', ...barStyle },
          { label: LL.coach.billing.recordedHistory.asleep(), data: this.bars.map(bar => bar.asleep), backgroundColor: asleepColor, stack: 'clients', ...barStyle },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          // the global crosshair of cs-progress-chart would draw a dashed line over the bars
          verticalCrosshair: false,
          legend: { display: false },
          tooltip: {
            backgroundColor: theme.tooltip.bg,
            titleColor: theme.tooltip.title,
            bodyColor: theme.tooltip.body,
            borderColor: theme.tooltip.border,
            borderWidth: 1,
            padding: 12,
            boxWidth: 7,
            boxHeight: 7,
            boxPadding: 4,
          },
        } as any,
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: { color: theme.tick, font: theme.font, maxRotation: 0, minRotation: 0 },
          },
          y: {
            stacked: true,
            min: 0,
            max: yMax.max,
            grid: { color: theme.grid, lineWidth: 1 },
            border: { display: false },
            ticks: { color: theme.tick, font: theme.font, stepSize: yMax.step, precision: 0 },
          },
        },
      },
      plugins: [capLinePlugin(caps, theme.tick)],
    });
  }

  /** A whole-number axis top with about 3 steps, high enough for every bar and the limit. */
  private niceAxisMax(): { max: number; step: number } {
    let top = 1;
    // for each month: the axis must reach its total and its limit
    for (const bar of this.bars) {
      top = Math.max(top, bar.active + bar.asleep, bar.cap ?? 0);
    }
    const step = Math.max(1, Math.ceil(top / 3));
    return { max: Math.ceil(top / step) * step, step };
  }
}

/**
 * Draws the plan limit as a dashed line across each month's full width, so the
 * months of one limit join into one line, as the mock-up draws it. A line
 * dataset would stop at the first and last bar's centres.
 */
function capLinePlugin(caps: (number | undefined)[], color: string): Plugin<'bar'> {
  return {
    id: 'coachClientsCapLine',
    afterDatasetsDraw: chart => {
      const x = chart.scales['x'];
      const y = chart.scales['y'];
      if (!x || !y || caps.every(cap => cap === undefined)) return;
      const halfWidth = caps.length > 0 ? (chart.chartArea.right - chart.chartArea.left) / caps.length / 2 : 0;
      const ctx = chart.ctx;
      ctx.save();
      ctx.lineWidth = CAP_LINE_WIDTH;
      ctx.setLineDash(CAP_LINE_DASH);
      ctx.strokeStyle = color;
      ctx.beginPath();
      // for each month with a limit: a segment across its full width
      caps.forEach((cap, index) => {
        if (cap === undefined) return;
        const center = x.getPixelForValue(index);
        const level = y.getPixelForValue(cap);
        ctx.moveTo(center - halfWidth, level);
        ctx.lineTo(center + halfWidth, level);
      });
      ctx.stroke();
      ctx.restore();
    },
  };
}
